import React, {ReactNode, useCallback, useEffect, useMemo, useState} from 'react';

import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {useShowFinalSortOption} from 'lookup-config/utils/useShowFinalSortOption';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {useTagSchemasWithRegistryQuery} from 'schemas/util/useTagSchemasQuery';
import Spinner from 'ui-library/Spinner';

import {FirstLookupStepFormProps} from './first-lookup-step/FirstLookupStepFormSection';
import inferTypeFromLookupConfig, {InferredLookupConfigType} from './inferTypeFromLookupConfig';
import LookupConfigBasePane from './LookupConfigBasePane';
import LookupConfigDetailPane, {getItemKey} from './LookupConfigDetailPane';
import {
  InjectedLookupStep,
  LookupConfigSpec,
  LookupStepInfo,
  LookupStepType,
  TaskFinderProcessTasksLookupStepSpec,
  LookupConfigModalContextInfo,
  LookupStepTreeItemsById,
  LookupSortType,
} from './LookupConfigModalConstants';
import {getLookupInfoArrayForLookupConfig} from './LookupConfigUtils';
import {getLookupStepInfoForProcessTaskStepItem, lookupStepsToInfos} from './LookupStepInitializerUtils';

export enum LookupConfigModalPane {
  LookupBase = 'LOOKUP_BASE',
  LookupDetail = 'LOOKUP_DETAIL',
}

const baseTestAttrs = {component: 'LookupConfigModal'};
export const DATA_TEST_ATTRS = {
  basePane: {...baseTestAttrs, element: 'basePane'},
  detailPane: {...baseTestAttrs, element: 'detailPane'},
  baseContinueButton: {...baseTestAttrs, element: 'baseContinueButton'},
  submitButton: {...baseTestAttrs, element: 'submitButton'},
};

export type LookupConfigModalPropsWithoutExtraState = LookupConfigModalProps<undefined>;

export interface BasePaneLookupObjectValidation {
  // optionally validate the current lookup config and render an error before continuing to the detail pane
  basePaneInvalidLookupObjectFn: (
    lookupStepGroup: Array<LookupStepInfo>,
    baseSchema: Schema | null
  ) => boolean;
  // optionally add callback that fires if there is an error when the base pane is submitted
  onBasePaneSubmissionError?: () => void;
  // return a custom error message component when basePaneInvalidLookupObjectFn returns an error
  basePaneInvalidLookupObjectErrorMessage?: () => JSX.Element;
}

export interface MultiLookupConfigSubmitItem<TExtraState> {
  lookupConfig: LookupConfigSpec;
  lookupConfigLabel?: string;
  finalStepName: string;
  finalStepSchemaField?: SchemaField | null;
  finalSortType: LookupSortType | undefined;
  extraState: TExtraState;
}

export interface LookupConfigModalProps<TExtraState> {
  /* Required props */
  // The schema from which to start the lookup.
  baseSchema?: Schema | null;
  // Allowed types for the first lookup step.
  firstLookupStepTypes: Array<LookupStepType>;
  // Allowed types for lookup steps other than the first and last.
  intermediateLookupStepTypes: Array<LookupStepType>;
  // Allowed types for the last step. This controls the overall type of the items looked up.
  finalLookupStepTypes: Array<LookupStepType>;
  /** finalSortType is a concept only on multiplex Notebook lookups. It is not persisted to the
   * LookupConfig, but instead to the Notebook's table column */
  finalSortType?: LookupSortType;
  // Called when modal is closed but not submitted.
  onRequestClose: () => void;
  // Called when modal is submitted. Always receives one entry per selected attribute; in the
  // common single-select case the array contains exactly one item.
  onRequestSubmit: (configs: Array<MultiLookupConfigSubmitItem<TExtraState>>) => void;

  /* Optional props */
  // Additional context for creating some LookupStep types
  context?: LookupConfigModalContextInfo;
  // Text to render at top of modal
  description?: ReactNode;
  // Validate that final lookup config is of expected type
  getLookupConfigValidationError?: (
    inferredType: InferredLookupConfigType,
    lookupConfig: LookupConfigSpec
  ) => string | null;
  // Extra state to track. This is an antipattern, do not add more use cases
  initialExtraState?: TExtraState;
  // Existing lookup config with which to populate the modal
  initialLookupConfig?: LookupConfigSpec | null;
  initialLookupConfigLabel?: string;
  // Pane that the modal should be opened to
  initialModalPane?: LookupConfigModalPane;
  // Custom first lookup step form to replace DefaultFirstLookupStepForm
  renderFirstLookupStepForm?: (props: FirstLookupStepFormProps<TExtraState>) => JSX.Element;
  // Function to filter out certain schema fields outside the tree
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean;
  // Function to filter out certain schema fields within the tree
  schemaFieldTreeFilterFn?: ((field: SchemaField) => boolean) | undefined;
  // Custom text for submit button
  submitButtonText?: string;
  // Custom modal title. Defaults to 'Configure lookup'
  title?: string;

  // Allow setting skipRows and skipColumns for WELLS and BOX_COORDINATES steps
  allowAlternatingGridPatterns?: boolean;
  // Add additional info for WELLS and BOX_COORDINATES steps connected to Lab Auto
  shouldShowNullForEmptyContentsTooltip?: boolean;
  // Allow configuring steps even if props.baseSchema is null
  allowMissingParentSchema?: boolean;
  // Allow setting a REPLICATES step at the end of the lookup config
  allowReplicatesStep?: boolean;
  // Always show first selected item in schema field tree even if there are no available link fields.
  shouldAlwaysShowFirstSelectedItemInTree?: boolean;
  // Hide FILTER step options
  shouldHideFilter?: boolean;
  // Hide previous step when on Detail pane
  shouldHideLinkToPreviousStepInDetailSelector?: boolean;
  // Allow setting lookupConfig.isMulti
  shouldShowIsMulti?: boolean;
  // Allow setting lookupConfig.multiFlattenLevel
  shouldShowIsMultiFlatten?: boolean;
  // Default value for lookupConfig.isMulti
  defaultIsMultiValue?: boolean;
  // Change copy to represent looking up schema fields only.
  useSchemaLanguage?: boolean;

  // this prop is used to define a set of additional lookup steps to inject into the LookupDetailSelector
  injectedFinalLookupSteps?: Array<InjectedLookupStep>;

  basePaneLookupObjectValidation?: BasePaneLookupObjectValidation;
  // Allow selecting multiple attributes in the detail pane; each produces one entry in the
  // onRequestSubmit array.
  allowMultipleDetailSelections?: boolean;
}

export default function QueryingLookupConfigModal<TExtraState>(
  props: LookupConfigModalProps<TExtraState>
): JSX.Element {
  const tagSchemasResult = useTagSchemasWithRegistryQuery();
  if (tagSchemasResult.type === QueryResultType.PENDING) {
    return <Spinner />;
  }
  return <LookupConfigModal<TExtraState> {...props} />;
}

function LookupConfigModal<TExtraState>(props: LookupConfigModalProps<TExtraState>): JSX.Element {
  const {baseLookupStepInfos, detailLookupStepInfo, replicatesLookupStepInfo, processTasksLookupStepInfo} =
    lookupStepsToInfos(
      props.initialLookupConfig ? props.initialLookupConfig.lookupSteps : [],
      props.baseSchema,
      props.context ?? {},
      props.schemaFieldTreeFilterFn,
      props.schemaFieldFilterFn,
      props.allowMissingParentSchema,
      props.shouldHideLinkToPreviousStepInDetailSelector,
      props.injectedFinalLookupSteps
    );
  const [lookupStepInfos, setLookupStepInfos] = useState(baseLookupStepInfos);
  const [isMulti, setIsMulti] = useState(
    props.initialLookupConfig
      ? props.initialLookupConfig.isMulti
      : props.defaultIsMultiValue != null
        ? props.defaultIsMultiValue
        : true
  );
  const [multiFlattenLevel, setMultiFlattenLevel] = useState(
    props.initialLookupConfig ? props.initialLookupConfig.multiFlattenLevel : null
  );
  const [lookupConfigLabel, setLookupConfigLabel] = useState(props.initialLookupConfigLabel);
  const [paneKey, setPaneKey] = useState(props.initialModalPane || LookupConfigModalPane.LookupBase);
  const [detailSteps, setDetailSteps] = useState<Array<LookupStepInfo>>(
    detailLookupStepInfo ? [detailLookupStepInfo] : []
  );
  const [lookupObjectSelected, setLookupObjectSelected] = useState(false);
  const [replicatesStep, setReplicatesStep] = useState(replicatesLookupStepInfo);
  const [initialDetailStep, setInitialDetailStep] = useState<LookupStepInfo | null>(
    detailLookupStepInfo ?? null
  );
  const [processTasksStep, setProcessTasksStep] = useState(
    props.context?.processTaskSchema != null
      ? processTasksLookupStepInfo ||
          (getLookupStepInfoForProcessTaskStepItem(
            props.context.processTaskSchema,
            props.baseSchema
          ) as LookupStepInfo & {lookupStep: TaskFinderProcessTasksLookupStepSpec})
      : null
  );
  const [selectTreeItemsById, setSelectTreeItemsById] = useState<LookupStepTreeItemsById>({});
  const [typecastSchemasByNodeId, setTypecastSchemasByNodeId] = useState<Map<string, Schema | null>>(
    new Map()
  );

  const updateTypecastSchemasByNodeId = useCallback(
    (nodeId: string, typecastRestrictedSchema: Schema | null): void => {
      const newIdToTypecast = new Map([...typecastSchemasByNodeId, [nodeId, typecastRestrictedSchema]]);
      setTypecastSchemasByNodeId(newIdToTypecast);
    },
    [typecastSchemasByNodeId]
  );

  const [extraState, setExtraState] = useState(props.initialExtraState as TExtraState);
  const [lookupConfigValidationError, setLookupConfigValidationError] = useState<string | null>();
  const isNotebookLookup = useMemo(
    () => props.firstLookupStepTypes[0] === LookupStepType.NOTEBOOK_COLUMN_TYPECHECK,
    [props.firstLookupStepTypes]
  );
  const shouldShowFinalSortOption = useShowFinalSortOption({
    detailSteps,
    isMulti,
    multiFlattenLevel,
    previousLookupStepInfos: lookupStepInfos,
    lookupObjectSelected,
    isNotebookLookup,
  });
  const [finalSortType, setFinalSortType] = useState<LookupSortType | undefined>(props.finalSortType);
  // default the finalSortType to alphabetical if we should show the sort option and it is previously unset
  // we want this to happen whenever we show the sort option for the first time
  useEffect(() => {
    if (shouldShowFinalSortOption && !finalSortType) {
      setFinalSortType(LookupSortType.ALPHABETICAL);
    }
  }, [finalSortType, shouldShowFinalSortOption]);

  useEffect(() => {
    // Any change to the selection invalidates a prior validation error — the previously assembled
    // config is no longer current. Submitting again will re-validate with the new selection.
    setLookupConfigValidationError(null);
  }, [detailSteps, lookupObjectSelected]);

  const handleToggleDetailStep = useCallback(
    (newStep: LookupStepInfo): void => {
      if (props.allowMultipleDetailSelections) {
        setDetailSteps((prev) => {
          const key = getItemKey(newStep);
          const exists = prev.some((s) => getItemKey(s) === key);
          return exists ? prev.filter((s) => getItemKey(s) !== key) : [...prev, newStep];
        });
      } else {
        setDetailSteps([newStep]);
        setLookupObjectSelected(false);
      }
    },
    [props.allowMultipleDetailSelections]
  );

  const handleUpdateDetailStep = useCallback((key: string, newStep: LookupStepInfo): void => {
    setDetailSteps((prev) => prev.map((s) => (getItemKey(s) === key ? newStep : s)));
  }, []);

  const handleSetLookupObjectSelected = useCallback(
    (selected: boolean): void => {
      setLookupObjectSelected(selected);
      if (selected && !props.allowMultipleDetailSelections) {
        setDetailSteps([]);
      }
    },
    [props.allowMultipleDetailSelections]
  );

  const handleSubmit = (): void => {
    const buildSingleConfig = (
      detailStep: LookupStepInfo | null
    ): {
      lookupConfig: LookupConfigSpec;
      finalStep: LookupStepInfo;
    } => {
      const finalLookupStepInfos = [...lookupStepInfos];
      if (detailStep) {
        finalLookupStepInfos.push(detailStep);
      }
      if (processTasksStep) {
        finalLookupStepInfos.push(processTasksStep);
      }
      if (replicatesStep) {
        finalLookupStepInfos.push(replicatesStep);
      }
      const lookupConfig = {
        isMulti,
        multiFlattenLevel,
        lookupSteps: getLookupInfoArrayForLookupConfig(finalLookupStepInfos).map(
          (stepInfo) => stepInfo.lookupStep
        ),
      };
      return {lookupConfig, finalStep: finalLookupStepInfos[finalLookupStepInfos.length - 1]};
    };

    const stepsToSubmit: Array<LookupStepInfo | null> = props.allowMultipleDetailSelections
      ? [...(lookupObjectSelected ? [null] : []), ...detailSteps]
      : [lookupObjectSelected ? null : (detailSteps[0] ?? null)];
    const configs: Array<MultiLookupConfigSubmitItem<TExtraState>> = [];
    for (const step of stepsToSubmit) {
      const {lookupConfig, finalStep} = buildSingleConfig(step);
      const {getLookupConfigValidationError} = props;
      if (getLookupConfigValidationError) {
        const inferredType = inferTypeFromLookupConfig(lookupConfig, props.baseSchema!);
        const err = getLookupConfigValidationError(inferredType, lookupConfig) ?? null;
        if (err != null) {
          setLookupConfigValidationError(err);
          return;
        }
      }
      configs.push({
        lookupConfig,
        lookupConfigLabel,
        finalStepName: finalStep.name,
        finalStepSchemaField: finalStep.schemaField,
        finalSortType: shouldShowFinalSortOption ? finalSortType : undefined,
        extraState,
      });
    }
    props.onRequestSubmit(configs);
  };

  // Guard against accidentally closing the modal
  const modalProps = {
    closeableOnBackgroundClick: false,
    closeableOnEscapeKey: false,
  };

  if (paneKey === LookupConfigModalPane.LookupBase) {
    return (
      <LookupConfigBasePane
        modalProps={modalProps}
        context={props.context}
        renderFirstLookupStepForm={props.renderFirstLookupStepForm}
        shouldAlwaysShowFirstSelectedItemInTree={props.shouldAlwaysShowFirstSelectedItemInTree}
        schemaFieldFilterFn={props.schemaFieldFilterFn}
        extraState={extraState}
        setExtraState={setExtraState}
        schemaFieldTreeFilterFn={props.schemaFieldTreeFilterFn}
        title={props.title ?? 'Configure lookup'}
        description={props.description}
        lookupStepInfos={lookupStepInfos}
        firstLookupStepTypes={props.firstLookupStepTypes}
        shouldHideFilter={props.shouldHideFilter}
        intermediateLookupStepTypes={props.intermediateLookupStepTypes}
        baseSchema={props.baseSchema ?? null}
        onEditLookupStepInfos={() => {
          setDetailSteps([]);
          setLookupObjectSelected(false);
          setInitialDetailStep(null);
        }}
        onGoToNextPane={(lookupStepInfos: Array<LookupStepInfo>) => {
          setLookupStepInfos(lookupStepInfos);
          setPaneKey(LookupConfigModalPane.LookupDetail);
        }}
        onRequestClose={props.onRequestClose}
        lookupConfigLabel={lookupConfigLabel}
        setLookupConfigLabel={setLookupConfigLabel}
        useSchemaLanguage={props.useSchemaLanguage}
        modalDataTestAttrs={DATA_TEST_ATTRS.basePane}
        continueButtonDataTestAttrs={DATA_TEST_ATTRS.baseContinueButton}
        allowAlternatingGridPatterns={props.allowAlternatingGridPatterns}
        shouldShowNullForEmptyContentsTooltip={props.shouldShowNullForEmptyContentsTooltip}
        basePaneLookupObjectValidation={props.basePaneLookupObjectValidation}
        selectTreeItemsById={selectTreeItemsById}
        setSelectTreeItemsById={setSelectTreeItemsById}
        typecastSchemasByNodeId={typecastSchemasByNodeId}
        updateTypecastSchemasByNodeId={updateTypecastSchemasByNodeId}
      />
    );
  } else {
    return (
      <LookupConfigDetailPane
        modalProps={modalProps}
        context={props.context}
        schemaFieldFilterFn={props.schemaFieldFilterFn}
        title={props.title ?? 'Configure lookup'}
        description={props.description}
        onGoToPrevPane={() => {
          setPaneKey(LookupConfigModalPane.LookupBase);
        }}
        onRequestClose={props.onRequestClose}
        onRequestSubmit={handleSubmit}
        previousLookupStepInfos={lookupStepInfos}
        baseSchema={props.baseSchema ?? null}
        shouldHideFilter={props.shouldHideFilter}
        finalLookupStepTypes={props.finalLookupStepTypes}
        initialDetailStep={initialDetailStep}
        detailSteps={detailSteps}
        lookupObjectSelected={lookupObjectSelected}
        onToggleDetailStep={handleToggleDetailStep}
        onUpdateDetailStep={handleUpdateDetailStep}
        onSetLookupObjectSelected={handleSetLookupObjectSelected}
        allowMultipleDetailSelections={props.allowMultipleDetailSelections}
        replicatesStep={replicatesStep}
        setReplicatesStep={setReplicatesStep}
        processTasksStep={processTasksStep}
        setProcessTasksStep={setProcessTasksStep}
        isMulti={isMulti}
        setIsMulti={setIsMulti}
        multiFlattenLevel={multiFlattenLevel}
        setMultiFlattenLevel={setMultiFlattenLevel}
        useSchemaLanguage={props.useSchemaLanguage}
        shouldHideLinkToPreviousStepInDetailSelector={props.shouldHideLinkToPreviousStepInDetailSelector}
        submitButtonText={props.submitButtonText}
        injectedFinalLookupSteps={props.injectedFinalLookupSteps}
        modalDataTestAttrs={DATA_TEST_ATTRS.detailPane}
        submitButtonDataTestAttrs={DATA_TEST_ATTRS.submitButton}
        lookupConfigValidationError={lookupConfigValidationError}
        shouldShowIsMulti={props.shouldShowIsMulti}
        shouldShowIsMultiFlatten={props.shouldShowIsMultiFlatten}
        shouldShowFinalSortOption={shouldShowFinalSortOption}
        allowReplicatesStep={props.allowReplicatesStep}
        allowAlternatingGridPatterns={props.allowAlternatingGridPatterns}
        finalSortType={finalSortType}
        setFinalSortType={setFinalSortType}
      />
    );
  }
}
